import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'

import { useSelector } from 'react-redux'
import { Modal } from 'antd'
import { isFunction } from 'lodash'

import { selectTranslate } from '../../../reducers/LocaleReducer/LocalReducer'
import { selectUser } from '../../../reducers/UserReducer'
import { InstitutionContext } from '../../InstitutionProvider'
import { GlobalContext } from '../../GlobalProvider'
import { createSchool, createSchoolSection, getSchoolSchoolSections } from '../../../utils/api/school'
import { onError, onSuccess } from '../../../utils/apiHelper'
import { getIdOrNull, isObject } from '../../../utils'
import { normalizeName } from '../../../utils/string'
import { getSchoolFromRequest } from '../../../utils/entities/internshipRequest'
import SchoolValidator from '../../../Components/shared/Schools/SchoolValidator'
import RequestSectionValidator from './RequestSectionValidator'

export const SchoolCompletionModalContext = createContext()

/** @enum {string} */
export const Steps = Object.freeze({
  SCHOOL_VALIDATION: 'SCHOOL_VALIDATION',
  SECTION_VALIDATION: 'SECTION_VALIDATION'
})

const DEFAULT_HANDLER = Object.freeze({ data: null, callback: null, step: null })

const LABELS = Object.freeze({
  title: 'request_school_validator.title',
  description: 'request_school_validator.description',
  duplicate: 'request_school_validator.duplicate_confirmation',
  merge: 'request_school_validator.merge_button',
  validate: 'common.validate'
})

const SchoolCompletionModal = ({ children }) => {
  const t = useSelector(selectTranslate)
  const user = useSelector(selectUser)
  const ic = useContext(InstitutionContext)
  const { countries } = useContext(GlobalContext) ?? {}

  const [formHandler, setFormHandler] = useState(DEFAULT_HANDLER)
  const [registeredSchool, setRegisteredSchool] = useState(null)
  const [schoolSections, setSchoolSections] = useState([])
  const [loading, setLoading] = useState(false)

  const isDataLoaded = isObject(formHandler.data)
  const school = registeredSchool ?? (isObject(formHandler.data?.school) ? formHandler.data.school : null)
  const step = formHandler.step ?? (school === null ? Steps.SCHOOL_VALIDATION : Steps.SECTION_VALIDATION)

  const close = useCallback(() => {
    setFormHandler(DEFAULT_HANDLER)
    setRegisteredSchool(null)
    setSchoolSections([])
    setLoading(false)
  }, [])

  const fetchSchoolSections = useCallback(school => getSchoolSchoolSections(user, school, { version: 'v2' })
    .then(({ data }) => Array.isArray(data) ? data.filter(ss => ss.sectionToYear) : [])
    .catch(() => []), [user])

  useEffect(() => {
    if (!isObject(school)) {
      return
    }

    let active = true

    fetchSchoolSections(school).then(sections => {
      if (active) {
        setSchoolSections(sections)
      }
    })

    return () => {
      active = false
    }
  }, [school, fetchSchoolSections])

  const complete = useCallback((school, sectionToYear = null, clearRequestedSection = false) => {
    if (isFunction(formHandler.callback)) {
      formHandler.callback(school, sectionToYear, clearRequestedSection)
    }

    close()
  }, [formHandler.callback, close])

  const handleSchoolSettled = useCallback(school => {
    setRegisteredSchool(school)
    setFormHandler(handler => ({ ...handler, step: Steps.SECTION_VALIDATION }))
  }, [])

  /** A school just registered runs no section, so there is none to hold the requested one against. */
  const handleCreateSchool = useCallback(school => {
    const requested = isObject(formHandler.data?.sectionToYear) ? formHandler.data.sectionToYear : null
    const sectionId = getIdOrNull(requested?.section)

    setLoading(true)

    createSchool(user, school.toRequestBody()).then(({ data }) => {
      if (!isObject(data)) {
        onError(t('school_form_modal.create.error'))

        return
      }

      onSuccess(t('school_form_modal.create.success'))

      if (isObject(ic) && isFunction(ic.setSchools)) {
        ic.setSchools(schools => schools.some(s => s.id === data.id) ? schools : [...schools, data])
      }

      if (sectionId === null) {
        handleSchoolSettled(data)

        return
      }

      return createSchoolSection(user, data, { id: sectionId }).then(() => {
        onSuccess(t('school_section.create.success'))
        complete(data, requested)
      }).catch(() => {
        onError(t('school_section.create.error'))
        handleSchoolSettled(data)
      })
    }).catch(() => onError(t('school_form_modal.create.error'))).finally(() => setLoading(false))
  }, [user, t, ic, formHandler.data, complete, handleSchoolSettled])

  /** Linking a section creates one school section per year : a request already naming its year keeps it. */
  const handleLinkSection = useCallback(sectionId => {
    const requested = isObject(formHandler.data?.sectionToYear) ? formHandler.data.sectionToYear : null

    setLoading(true)

    createSchoolSection(user, school, { id: sectionId }).then(() => {
      onSuccess(t('school_section.create.success'))

      return fetchSchoolSections(school)
    }).then(sections => {
      if (requested !== null) {
        complete(school, requested)

        return
      }

      const year = normalizeName(formHandler.data?.schoolYearName)
      const linked = sections.filter(ss => getIdOrNull(ss.sectionToYear?.section) === sectionId)
      const matched = linked.find(ss => normalizeName(ss.sectionToYear?.schoolYearName) === year)?.sectionToYear ?? null

      if (matched !== null) {
        complete(school, matched)

        return
      }

      setSchoolSections(sections)
    }).catch(() => onError(t('school_section.create.error'))).finally(() => setLoading(false))
  }, [user, t, school, formHandler.data, fetchSchoolSections, complete])

  const handleMergeSection = useCallback(schoolSection => {
    if (isObject(schoolSection)) {
      complete(school, schoolSection.sectionToYear)
    }
  }, [school, complete])

  const isSectionStep = step === Steps.SECTION_VALIDATION

  const describedSchool = useMemo(
    () => isDataLoaded ? getSchoolFromRequest(formHandler.data) : null,
    [isDataLoaded, formHandler.data]
  )

  const context = useMemo(() => ({ setFormHandler }), [])

  return (
    <>
      <SchoolCompletionModalContext.Provider value={context}>
        {children}
      </SchoolCompletionModalContext.Provider>
      <Modal
        visible={isDataLoaded}
        width={700}
        title={t(`school_completion_modal.title.${(isSectionStep ? Steps.SECTION_VALIDATION : Steps.SCHOOL_VALIDATION).toLowerCase()}`)}
        footer={null}
        onCancel={close}
      >
        {isDataLoaded && !isSectionStep && (
          <SchoolValidator
            disabled={loading}
            editable
            labels={LABELS}
            countries={countries}
            school={describedSchool}
            schools={isObject(ic) ? ic.schools : []}
            onMerge={handleSchoolSettled}
            onValidate={handleCreateSchool}
          />
        )}
        {isDataLoaded && isSectionStep && (
          <RequestSectionValidator
            disabled={loading}
            request={formHandler.data}
            schoolSections={schoolSections}
            onMerge={handleMergeSection}
            onSkip={() => complete(school, null, true)}
            onValidate={handleLinkSection}
          />
        )}
      </Modal>
    </>
  )
}

export default SchoolCompletionModal
